/**
 * The organization tree, drawn once for everything that picks places.
 *
 * `docs/scope-switcher.md` describes the header's switcher; this is the part
 * of it that is not the header. A field that assigns a service to branches
 * (`docs/services-and-branch-scope.md` §3) wants the same tree, the same kind
 * tiles, the same cascading ticks and the same meta line — and wants none of
 * the switcher's recents, its "only", its keyboard or its apply-on-close,
 * because those are what the *header* does with a selection.
 *
 * So the drawing lives here and the deciding stays with each caller.
 * `shared/src/scope.ts` remains the one cascade underneath both: `stateOf`,
 * `toggle`, `normalise`, `coveredBranches`, `scopeLabelOf`. Nothing in this
 * file knows what a selection is *for*.
 */
import {
  buildScopeTree, countAt, stateOf,
  type OrgUnitSummary, type ScopeTree, type ScopeUnit, type TriState,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Checkbox, Tag } from '../components/primitives.js'
import { KindTile } from '../builder/kind-tile.js'
import { IconChevronDown, IconChevronRight } from '../frame/icons.js'

/** The one place the tree's shape is derived from what `/me` sent. */
export function treeOf(available: OrgUnitSummary[]): ScopeTree {
  return buildScopeTree(available.map((unit): ScopeUnit => ({
    id: unit.id,
    name: unit.name,
    kind: unit.kind,
    parentId: unit.parentId,
    reachable: unit.reachable,
  })))
}

/**
 * Which places are worth offering.
 *
 * A closed branch and a region with nothing under it are both dead ends: one
 * has no work left to file, the other never had any, and both sit in a list
 * somebody is scanning to find the place they actually want. So the picker
 * leaves them out — the region entirely, the closed branch until it is asked
 * for, because a branch that shut in March still has a year of jobs behind it
 * that somebody will need to look at.
 *
 * Kept, rather than filtered row by row, because the emptiness is the whole
 * subtree's: a district holding one closed branch is as empty as a region
 * holding no district. Everything that is an ancestor of a branch worth
 * offering stays; nothing else does.
 */
export function offerable(available: OrgUnitSummary[], showClosed: boolean): OrgUnitSummary[] {
  const full = treeOf(available)

  const branches = available.filter((unit) =>
    unit.kind === 'branch' && unit.reachable && (showClosed || !unit.closed))

  const keep = new Set(branches.map((unit) => unit.id))
  for (const branch of branches) for (const up of full.ancestorsOf(branch.id)) keep.add(up)

  /* A company with no open branch at all still needs a picker to open. */
  if (keep.size === 0) return available
  return available.filter((unit) => keep.has(unit.id))
}

/* "branches", not "branchs" — the only irregular word this file counts. */
export const plural = (n: number, word: string) =>
  `${n} ${n === 1 ? word : word.endsWith('ch') ? `${word}es` : `${word}s`}`

/**
 * The line under a name.
 *
 * Counts are of what this person reaches, which is why a district manager's
 * district says "2 branches": two is their whole world there, and the
 * company's nine would be a number they can do nothing with.
 */
export function metaOf(unit: ScopeUnit, fact: OrgUnitSummary | undefined, tree: ScopeTree): string {
  if (!unit.reachable) return 'no access · shown for context'

  const branches = fact?.branchCount ?? 0

  if (unit.kind === 'branch') {
    const parts = [fact?.yardName, fact?.crewCount ? plural(fact.crewCount, 'crew') : null]
    return parts.filter(Boolean).join(' · ') || 'no yard yet'
  }

  /* §1.8: the top of somebody's reach says so, once. The company needs no
     such note — it is everything by definition. */
  const above = unit.parentId ? tree.byId.get(unit.parentId) : null
  const mine = unit.kind !== 'company' && !above?.reachable ? ' · your access' : ''

  if (unit.kind === 'company') return `the whole company · ${plural(branches, 'branch')}`
  if (branches === 0) return 'no branches yet'
  if (unit.kind === 'region') {
    return `${plural(fact?.districtCount ?? 0, 'district')} · ${plural(branches, 'branch')}${mine}`
  }
  return `${plural(branches, 'branch')}${mine}`
}

/**
 * Which groups start open.
 *
 * Everything with children, except a district that is fully on *because of a
 * root above it* — that subtree needs no inspection, and four branch rows
 * each saying "yes, still on" is four rows between the reader and the place
 * they came to find. A district that is itself the thing ticked stays open,
 * because it is what the person chose and they may be about to change it.
 */
export function defaultOpen(roots: string[], tree: ScopeTree): Set<string> {
  const open = new Set<string>()
  for (const unit of tree.byId.values()) {
    if (unit.kind === 'branch') continue
    const settled = unit.kind === 'district'
      && !roots.includes(unit.id)
      && stateOf(unit.id, roots, tree) === 'on'
    if (!settled) open.add(unit.id)
  }
  return open
}

export interface Row {
  unit: ScopeUnit
  fact: OrgUnitSummary | undefined
  depth: number
  hasChildren: boolean
  open: boolean
}

/**
 * The tree, flattened to the rows actually drawn.
 *
 * A search shows every match *and the places above it*, opened, so a result
 * is never a name floating with no idea where it lives — "Reading" on its own
 * is a word, "East Coast › Lehigh Valley › Reading" is an answer.
 */
export function rowsOf(
  tree: ScopeTree, facts: Map<string, OrgUnitSummary>, open: Set<string>, query: string,
): Row[] {
  const needle = query.trim().toLowerCase()
  const matches = (unit: ScopeUnit): boolean =>
    unit.name.toLowerCase().includes(needle)
    || (facts.get(unit.id)?.code ?? '').toLowerCase().includes(needle)

  const keep = new Set<string>()
  if (needle) {
    for (const unit of tree.byId.values()) {
      if (!matches(unit)) continue
      keep.add(unit.id)
      for (const up of tree.ancestorsOf(unit.id)) keep.add(up)
    }
  }

  const rows: Row[] = []
  const walk = (unit: ScopeUnit, depth: number): void => {
    if (needle && !keep.has(unit.id)) return

    const children = tree.children.get(unit.id) ?? []
    const isOpen = needle ? true : open.has(unit.id)
    rows.push({
      unit, fact: facts.get(unit.id), depth, hasChildren: children.length > 0, open: isOpen,
    })
    if (!isOpen) return
    for (const child of children) walk(child, depth + 1)
  }
  for (const root of tree.roots) walk(root, 0)
  return rows
}


export function ScopeRow({
  row, tree, roots, focused, onToggle, onToggleOpen, onOnly,
}: {
  row: Row
  tree: ScopeTree
  roots: string[]
  focused?: boolean
  onToggle: () => void
  onToggleOpen: () => void
  /**
   * The header's fast way from the company to one place.
   *
   * Left out by a field, where "only this branch" is what ticking one
   * branch already means — there is no current selection to replace, so a
   * second word for the same click is a word to read and ignore.
   */
  onOnly?: () => void
}) {
  const { unit, fact, depth, hasChildren, open } = row
  const state: TriState = stateOf(unit.id, roots, tree)
  const count = countAt(unit.id, roots, tree)
  const disabled = !unit.reachable || count.total === 0

  return (
    <div
      role="option"
      aria-selected={state === 'on'}
      data-unit={unit.id}
      className={cn(
        'group mx-1.5 flex h-10 items-center gap-[9px] rounded-control pr-2.5',
        disabled ? 'opacity-50' : 'cursor-pointer hover:bg-hover',
        focused && !disabled && 'bg-hover',
      )}
      style={{ paddingLeft: 10 + 22 * depth }}
      onClick={() => { if (!disabled) onToggle() }}
    >
      <Checkbox
        checked={state === 'on'}
        indeterminate={state === 'mixed'}
        disabled={disabled}
        onChange={() => { if (!disabled) onToggle() }}
        onClick={(event) => event.stopPropagation()}
        label={unit.name}
        hideLabel
      />

      <span className="flex w-3.5 shrink-0 text-text-faint">
        {hasChildren && (
          <button
            type="button"
            aria-label={open ? `Collapse ${unit.name}` : `Expand ${unit.name}`}
            onClick={(event) => { event.stopPropagation(); onToggleOpen() }}
          >
            {open ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />}
          </button>
        )}
      </span>

      <KindTile kind={unit.kind} size={26} />

      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cn('flex min-w-0 items-center gap-1.5 truncate',
          unit.kind === 'company' ? 'text-card' : 'text-row font-semibold')}>
          <span className="truncate">{unit.name}</span>
          {fact?.closed && <Tag>Closed</Tag>}
        </span>
        <span className="truncate text-sub text-text-muted">{metaOf(unit, fact, tree)}</span>
      </span>

      {unit.kind !== 'branch' && count.total > 0 && (
        <span className={cn('shrink-0 text-chip tabular-nums',
          state === 'on' ? 'text-accent' : 'text-text-faint')}>
          {count.on} of {count.total}
        </span>
      )}

      {/* §1.7: the fast way from the company to one place. */}
      {!disabled && onOnly && (
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); onOnly() }}
          className={cn(
            'ml-2 hidden shrink-0 text-chip font-semibold text-accent',
            'group-hover:inline focus-visible:inline',
          )}
        >
          only
        </button>
      )}
    </div>
  )
}


export function NoMatch({ query }: { query: string }) {
  return (
    <div className="flex flex-col gap-1 px-4 py-[18px]">
      <b className="text-card">Nothing called “{query.trim()}”</b>
      <span className="text-cell text-text-muted">
        Branches, districts and regions are set up under Settings → Organization.
      </span>
    </div>
  )
}

